import { useState, useEffect, useCallback } from 'react';
import notificationService from '../services/notificationService';
import { message } from 'antd';

/**
 * Custom hook for managing notifications
 * Provides real-time notification management with automatic updates
 */
export const useNotifications = (options = {}) => {
  const {
    autoRefresh = true,
    refreshInterval = 30000, // 30 seconds
    limit = 10
  } = options;

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await notificationService.getNotifications({ 
        limit, 
        ...params 
      });
      
      if (response.success) {
        setNotifications(response.data.notifications);
        return response.data;
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setError('Failed to load notifications');
      message.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationService.getUnreadCount();
      if (response.success) {
        setUnreadCount(response.data.count);
        return response.data.count;
      }
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      
      // Update local state optimistically
      setNotifications(prev => 
        prev.map(n => n._id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      message.success('Notification marked as read');
      return true;
    } catch (err) {
      console.error('Failed to mark as read:', err);
      message.error('Failed to mark notification as read');
      return false;
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      
      message.success('All notifications marked as read');
      return true;
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      message.error('Failed to mark all notifications as read');
      return false;
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
      
      // Update local state
      const deletedNotification = notifications.find(n => n._id === notificationId);
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
      
      if (deletedNotification && !deletedNotification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      message.success('Notification deleted');
      return true;
    } catch (err) {
      console.error('Failed to delete notification:', err);
      message.error('Failed to delete notification');
      return false;
    }
  }, [notifications]);

  // Refresh all data
  const refresh = useCallback(async () => {
    await Promise.all([
      fetchNotifications(),
      fetchUnreadCount()
    ]);
  }, [fetchNotifications, fetchUnreadCount]);

  // Create test notifications (for development)
  const createTestNotifications = useCallback(async () => {
    try {
      // For now, just create some mock notifications locally
      // This can be replaced with actual API call when backend endpoint is implemented
      console.log('Test notifications feature - backend endpoint not implemented yet');
      
      // Simulate successful creation
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh notifications
      await fetchNotifications();
      
      return { success: true, message: 'Test notifications created (mock)' };
    } catch (error) {
      console.error('Failed to create test notifications:', error);
      throw error;
    }
  }, [fetchNotifications]);

  // Initialize and set up auto-refresh
  useEffect(() => {
    // Initial fetch
    fetchNotifications();
    fetchUnreadCount();

    // Set up auto-refresh if enabled
    let interval;
    if (autoRefresh && refreshInterval > 0) {
      interval = setInterval(() => {
        fetchNotifications();
        fetchUnreadCount();
      }, refreshInterval);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [fetchNotifications, fetchUnreadCount, autoRefresh, refreshInterval]);

  return {
    // Data
    notifications,
    unreadCount,
    loading,
    error,
    
    // Actions
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh,
    createTestNotifications,
    
    // Computed
    hasUnread: unreadCount > 0,
    totalCount: notifications.length
  };
};

export default useNotifications;
